import {
  Box,
  Divider,
  Flex,
  FormLabel,
  Icon,
  Input,
  List,
  ListItem,
  Select,
  Switch,
  Text,
  Tooltip,
} from '@chakra-ui/react';
import SectionWrapper from '../../pass-templates-new/SectionWrapper';
import { CustomInfoOutlineIcon } from '../../../../theme/icons/CustomInfoOutlineIcon';
import { CustomErrorIcon } from '../../../../theme/icons/CustomErrorIcon';
import {
  BARCODE_TYPE_LABELS,
  NFC_SOURCE_LABELS,
  type PassBarcode,
} from '../../../../store/types/pass';

const BARCODE_TYPES = [
  { label: 'QR', value: 'qr' },
  { label: 'CODE128', value: 'code128' },
  { label: 'AZTEC', value: 'aztec' },
  { label: 'PDF417', value: 'pdf417' },
];

const BARCODE_SOURCES = [
  { label: 'UUID', value: 'uuid' },
  { label: 'External ID', value: 'extId' },
  { label: 'Custom', value: 'custom' },
];

const NFC_SOURCES = [
  { label: 'UUID', value: 'uuid' },
  { label: 'External ID', value: 'extId' },
  { label: 'Custom', value: 'custom' },
];

const BarcodeTypeTooltip = () => (
  <Tooltip
    label={
      <List spacing={1} styleType="disc" pl={3}>
        <ListItem>
          <Text as="span" fontWeight="bold">
            QR
          </Text>{' '}
          is recommended for most setups and works with all readers
        </ListItem>
        <ListItem>
          <Text as="span" fontWeight="bold">
            PDF417
          </Text>{' '}
          is the standard for airlines
        </ListItem>
        <ListItem>
          <Text as="span" fontWeight="bold">
            Aztec
          </Text>{' '}
          is compact and suited to rail and transit
        </ListItem>
        <ListItem>
          <Text as="span" fontWeight="bold">
            Code 128
          </Text>{' '}
          is a linear barcode used in retail and logistics
        </ListItem>
      </List>
    }
    hasArrow
    placement="top"
    fontSize="12px"
    bg="inkBlue"
    color="white"
    borderRadius="6px"
    p="10px"
  >
    <Box
      as="span"
      display="inline-flex"
      alignItems="center"
      lineHeight="1"
      cursor="pointer"
    >
      <Icon as={CustomInfoOutlineIcon} fill="grey" boxSize="15px" />
    </Box>
  </Tooltip>
);

const EditFieldRow = ({
  label,
  labelElement,
  children,
  isRequired = false,
}: {
  label: string;
  labelElement?: React.ReactNode;
  children: React.ReactNode;
  isRequired?: boolean;
}) => (
  <Box>
    {labelElement || (
      <Text
        textStyle="bodyFamilyRegular"
        fontSize="16px"
        color="black"
        mb="6px"
      >
        {label}
        {isRequired && (
          <Text color="red" fontSize="12px" ml="1px" as="span">
            *
          </Text>
        )}
      </Text>
    )}
    <Box
      bg="rgba(228, 228, 228, 0.5)"
      borderRadius="5px"
      h="35px"
      display="flex"
      alignItems="center"
      px="14px"
    >
      {children}
    </Box>
  </Box>
);

const deriveNfc = (
  nfcRaw: string | null | undefined,
  passId?: string,
  extId?: string
) => {
  if (!nfcRaw) return null;
  const source =
    nfcRaw === extId ? 'extId' : nfcRaw === passId ? 'uuid' : 'custom';
  return { source, value: nfcRaw };
};

interface ScanningSectionProps {
  isEdit?: boolean;
  // View mode props
  barcode?: PassBarcode;
  nfcRaw?: string | null;
  passId?: string;
  extId?: string;
  // Edit mode props
  nfcEnabled?: boolean;
  nfcSource?: string;
  nfcCustomValue?: string;
  nfcEntityEnabled?: boolean;
  errors?: Record<string, string>;
  onBarcodeChange?: (barcode: Partial<PassBarcode>) => void;
  onNfcEnabledChange?: (enabled: boolean) => void;
  onNfcSourceChange?: (source: string) => void;
  onNfcCustomValueChange?: (value: string) => void;
  onFieldFocus?: (identifier: string) => void;
  focusedField?: string | null;
}

const ScanningSection = ({
  isEdit,
  barcode,
  nfcRaw,
  passId,
  extId,
  nfcEnabled = false,
  nfcSource = 'uuid',
  nfcCustomValue = '',
  nfcEntityEnabled = true,
  errors = {},
  onBarcodeChange,
  onNfcEnabledChange,
  onNfcSourceChange,
  onNfcCustomValueChange,
  onFieldFocus,
  focusedField,
}: ScanningSectionProps) => {
  const nfc = !isEdit ? deriveNfc(nfcRaw, passId, extId) : null;

  return (
    <SectionWrapper
      title="Scanning"
      subtitle={
        isEdit ? (
          <Text fontSize="15px" color="grey">
              Control how this pass is scanned at entry points and/or redeems
              rewards
          </Text>
        ) : undefined
      }
      tooltip={
        isEdit
          ? 'Let users scan their pass for instant identification - you can use the unique UUID of the pass, external ID or a custom value.'
          : 'Choose how this pass is scanned at the point of sale. NFC requires an Apple-approved certificate.'
      }
      openOnLoad
      id="nfc-and-barcode-section"
    >
      {isEdit ? (
        <Box display="flex" flexDirection="column" gap={4}>
            <Flex justify="space-between" align="center">
              <Text textStyle="bodyFamilyRegular" fontSize="16px" color="black">
                Barcode
              </Text>
              <Switch
                isChecked={barcode?.enabled ?? false}
                onChange={(e) => {
                  onFieldFocus?.('barcode.enabled');
                  onBarcodeChange?.({ enabled: e.target.checked });
                }}
                sx={{
                  'span.chakra-switch__track[data-checked]': { bg: '#505E69' },
                }}
              />
            </Flex>

            <Flex justify="space-between" align="center">
              <Text textStyle="bodyFamilyRegular" fontSize="16px" color="black">
                NFC
              </Text>
              <Tooltip
                label="NFC access is restricted. Contact support@passentry.com to request access"
                isDisabled={nfcEntityEnabled}
                hasArrow
                placement="top"
                fontSize="12px"
                bg="inkBlue"
                color="white"
                borderRadius="6px"
                textAlign="center"
                p="10px"
              >
                <Box>
                  <Switch
                    isChecked={nfcEnabled}
                    isDisabled={!nfcEntityEnabled}
                    onChange={(e) => {
                      onFieldFocus?.('nfc.enabled');
                      onNfcEnabledChange?.(e.target.checked);
                    }}
                    sx={{
                      'span.chakra-switch__track[data-checked]': {
                        bg: '#505E69',
                      },
                    }}
                  />
                </Box>
              </Tooltip>
            </Flex>

          {barcode?.enabled && (
            <>
              <Divider />

              <EditFieldRow
                  label="Barcode type"
                  labelElement={
                    <Flex align="center" gap={1} mb="6px">
                      <Text
                        textStyle="bodyFamilyRegular"
                        fontSize="16px"
                        color="black"
                      >
                        Barcode type
                      </Text>
                      <BarcodeTypeTooltip />
                    </Flex>
                  }
                >
                  <Select
                    variant="unstyled"
                    size="sm"
                    fontSize="14px"
                    value={barcode.type || ''}
                    onFocus={() => onFieldFocus?.('barcode.type')}
                    onChange={(e) =>
                      onBarcodeChange?.({
                        type: e.target.value as PassBarcode['type'],
                      })
                    }
                  >
                    <option value="" disabled>
                      Select type
                    </option>
                    {BARCODE_TYPES.map((t) => (
                      <option key={t.value} value={t.value}>
                        {t.label}
                      </option>
                    ))}
                  </Select>
                </EditFieldRow>
              {errors['barcode.type'] && (
                <Flex align="center" gap={1.5} pl={2} mt={-2}>
                  <CustomErrorIcon boxSize="12px" color="#E53E3E" />
                  <Text fontSize="12px" color="#E53E3E">
                    {errors['barcode.type']}
                  </Text>
                </Flex>
              )}
              <EditFieldRow label="Barcode source">
                  <Select
                    variant="unstyled"
                    size="sm"
                    fontSize="14px"
                    value={barcode.source || ''}
                    onFocus={() => onFieldFocus?.('barcode.source')}
                    onChange={(e) => {
                      const source = e.target.value as PassBarcode['source'];
                      onBarcodeChange?.(
                        source === 'custom' ? { source } : { source, value: '' }
                      );
                    }}
                  >
                    <option value="" disabled>
                      Select source
                    </option>
                    {BARCODE_SOURCES.map((s) => (
                      <option key={s.value} value={s.value}>
                        {s.label}
                      </option>
                    ))}
                  </Select>
                </EditFieldRow>
              {errors['barcode.source'] && (
                <Flex align="center" gap={1.5} pl={2} mt={-2}>
                  <CustomErrorIcon boxSize="12px" color="#E53E3E" />
                  <Text fontSize="12px" color="#E53E3E">
                    {errors['barcode.source']}
                  </Text>
                </Flex>
              )}
              {barcode.source === 'custom' && (
                <>
                    <EditFieldRow label="Barcode value" isRequired>
                      <Input
                        variant="unstyled"
                        textStyle="bodyFamilyRegular"
                        fontSize="14px"
                        h="full"
                        value={barcode.value || ''}
                        placeholder="Enter custom value"
                        onFocus={() => onFieldFocus?.('barcode.value')}
                        onChange={(e) =>
                          onBarcodeChange?.({ value: e.target.value })
                        }
                      />
                    </EditFieldRow>
                  {errors['barcode.value'] && (
                    <Flex align="center" gap={1.5} pl={2} mt={-2}>
                      <CustomErrorIcon boxSize="12px" color="#E53E3E" />
                      <Text fontSize="12px" color="#E53E3E">
                        {errors['barcode.value']}
                      </Text>
                    </Flex>
                  )}
                </>
              )}
              <Flex justify="space-between" align="center">
                  <Text
                    textStyle="bodyFamilyRegular"
                    fontSize="14px"
                    color="black"
                  >
                    Display value
                  </Text>
                  <Switch
                    isChecked={barcode.displayText ?? false}
                    onChange={(e) => {
                      onFieldFocus?.('barcode.displayText');
                      onBarcodeChange?.({ displayText: e.target.checked });
                    }}
                    sx={{
                      'span.chakra-switch__track[data-checked]': {
                        bg: '#505E69',
                      },
                    }}
                  />
                </Flex>
            </>
          )}

          {nfcEnabled && (
            <>
              <Divider />

              <EditFieldRow label="NFC source">
                  <Select
                    variant="unstyled"
                    size="sm"
                    fontSize="14px"
                    value={nfcSource}
                    onFocus={() => onFieldFocus?.('nfc.source')}
                    onChange={(e) => onNfcSourceChange?.(e.target.value)}
                  >
                    {NFC_SOURCES.map((s) => (
                      <option key={s.value} value={s.value}>
                        {s.label}
                      </option>
                    ))}
                  </Select>
                </EditFieldRow>
              {nfcSource === 'custom' && (
                <>
                    <EditFieldRow label="NFC value" isRequired>
                      <Input
                        variant="unstyled"
                        textStyle="bodyFamilyRegular"
                        fontSize="14px"
                        h="full"
                        value={nfcCustomValue}
                        placeholder="Enter custom NFC value"
                        onFocus={() => onFieldFocus?.('nfc.customValue')}
                        onChange={(e) =>
                          onNfcCustomValueChange?.(e.target.value)
                        }
                      />
                    </EditFieldRow>
                  {errors.nfcCustomValue && (
                    <Flex align="center" gap={1.5} pl={2} mt={-2}>
                      <CustomErrorIcon boxSize="12px" color="#E53E3E" />
                      <Text fontSize="12px" color="#E53E3E">
                        {errors.nfcCustomValue}
                      </Text>
                    </Flex>
                  )}
                </>
              )}
            </>
          )}
        </Box>
      ) : (
        <Box>
          {barcode?.enabled && (
            <>
              <Flex align="center" gap={1}>
                <FormLabel variant="templateBuilderLabel" mb={0} mr={0}>
                  Barcode type
                </FormLabel>
                <BarcodeTypeTooltip />
              </Flex>
              <Text textStyle="bodyRegular" fontSize="16px" color="#989898">
                {BARCODE_TYPE_LABELS[barcode.type]}
              </Text>
              <FormLabel variant="templateBuilderLabel" mt={6}>
                Barcode value
              </FormLabel>
              <Text textStyle="bodyRegular" fontSize="16px" color="#989898">
                {barcode.value}
              </Text>
            </>
          )}
          {nfc && (
            <>
              {barcode?.enabled && <Divider my={6} />}
              <FormLabel variant="templateBuilderLabel">NFC source</FormLabel>
              <Text textStyle="bodyRegular" fontSize="16px" color="#989898">
                {NFC_SOURCE_LABELS[nfc.source]}
              </Text>
              <FormLabel variant="templateBuilderLabel" mt={6}>
                NFC value
              </FormLabel>
              <Text textStyle="bodyRegular" fontSize="16px" color="#989898">
                {nfc.value}
              </Text>
            </>
          )}
        </Box>
      )}
    </SectionWrapper>
  );
};

export default ScanningSection;
